import { DeleteCartProduct } from 'assets/Icons';
import { ProductQtyCounter } from 'components/atoms';
import { performRemoveProductFromCart, performUpdateCartItemQty } from 'components/molecules/Goods/utils';
import QUERY_KEYS from 'network/config/queryKeys';
import { removeProductFromCart, updateCartItemQty } from 'network/mutations/products';
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import { userRoleAtom } from 'store/atoms/userAtom';
import { formatCurrency } from 'utils/currencyFormater';
import { CheckoutProductCon } from './style';

type CheckoutProductProp = {
  item: any;
  orderId: any;
  customerType?: any;
};

// Debounce utility function
const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Retry utility for optimistic locking errors
const retryOnOptimisticLock = async <T,>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 100
): Promise<T> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      const isOptimisticLockError =
        error?.response?.data?.message?.includes('ObjectOptimisticLockingFailureException') ||
        error?.message?.includes('ObjectOptimisticLockingFailureException');

      if (isOptimisticLockError && attempt < maxRetries) {
        // Exponential backoff
        const delay = baseDelay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      throw error;
    }
  }
  throw new Error('Max retries exceeded');
};

export const CheckoutProduct = ({ item, orderId, customerType }: CheckoutProductProp) => {
  const queryClient = useQueryClient();
  const userRole = useRecoilValue(userRoleAtom);
  const { price, bulkOrderPrice, images, productName, quantity: initialQuantity, productId } = item;
  const { mutateAsync: removeFn, isLoading: deleting } = useMutation({ mutationFn: removeProductFromCart });
  const { mutateAsync: updateMutate } = useMutation({ mutationFn: updateCartItemQty });

  const [quantity, setQuantity] = useState(initialQuantity);
  const [isUpdating, setIsUpdating] = useState(false);

  const deleteItem = () => {
    performRemoveProductFromCart(item, removeFn, queryClient, customerType, orderId);
    queryClient.invalidateQueries(QUERY_KEYS.CART);
  };

  // Debounced quantity update function
  const debouncedQuantityUpdate = useMemo(
    () =>
      debounce(async (newQuantity: number) => {
        if (isUpdating) return;

        setIsUpdating(true);
        try {
          await retryOnOptimisticLock(async () => {
            if (newQuantity < 1) {
              // Remove item completely when quantity goes below 1
              await performRemoveProductFromCart(item, removeFn, queryClient, customerType, orderId);
            } else {
              await performUpdateCartItemQty(
                newQuantity,
                { productId },
                orderId,
                updateMutate,
                removeFn,
                customerType,
                queryClient
              );
            }
          });
        } catch (error) {
          console.error('Failed to update quantity after retries:', error);
          // Revert to previous quantity on failure
          setQuantity(initialQuantity);
        } finally {
          setIsUpdating(false);
        }
      }, 300),
    [productId, orderId, updateMutate, removeFn, customerType, queryClient, isUpdating, initialQuantity]
  );

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      // Update UI immediately for responsiveness
      setQuantity(newQuantity);

      // Debounced API call
      debouncedQuantityUpdate(newQuantity);
    },
    [debouncedQuantityUpdate]
  );

  const calculatePrice = () => {
    let amount;
    if (customerType === 'BUSINESS') {
      amount = bulkOrderPrice * quantity;
    } else {
      amount = price * quantity;
    }

    return formatCurrency(amount);
  };

  return (
    <div>
      <CheckoutProductCon>
        <div className="imageCon">
          <img src={Array.isArray(images) ? images[0] : images ?? '/graphics/madina.svg'} alt="productImage" />
        </div>
        <div className="productDetailsCon">
          <div className="top flex items-start justify-between">
            <div className="desc">
              <p>{productName}</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm text-gray-600">QTY:</span>
                <ProductQtyCounter
                  value={quantity}
                  setValue={handleQuantityChange}
                  customerType={customerType}
                  max={item.quantityAvailable}
                  minimumOrderQuantity={item.minimumOrderQuantity}
                  disabled={isUpdating}
                />
              </div>
            </div>
            <button disabled={deleting} onClick={deleteItem} className="pt-2">
              <DeleteCartProduct />
            </button>
          </div>
          <div className="price">{calculatePrice()}</div>
        </div>
      </CheckoutProductCon>
    </div>
  );
};
